// The in-memory record store the UI reads from. Every change is written to
// the local database and flagged dirty for the sync engine. Reads are
// synchronous and snapshot-stable so React can use useSyncExternalStore.

import { recordKey, wins, type SyncRecord } from '../domain/sync.ts';
import type { CollectionName, Collections } from '../domain/types.ts';
import type { LocalDB, StoredRecord } from './idb.ts';

type Listener = () => void;
type Doc<C extends CollectionName> = Collections[C];

export class Store {
  private records = new Map<string, StoredRecord>();
  private listCache = new Map<CollectionName, unknown[]>();
  private listeners = new Set<Listener>();
  private dirtyListeners = new Set<Listener>();
  private _version = 0;

  private db: LocalDB;
  readonly deviceId: string;

  constructor(db: LocalDB, deviceId: string) {
    this.db = db;
    this.deviceId = deviceId;
  }

  async load(): Promise<void> {
    for (const r of await this.db.loadAll()) this.records.set(r.key, r);
    this.changed();
  }

  get version(): number {
    return this._version;
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  onDirty(listener: Listener): () => void {
    this.dirtyListeners.add(listener);
    return () => this.dirtyListeners.delete(listener);
  }

  get<C extends CollectionName>(col: C, id: string): Doc<C> | undefined {
    const r = this.records.get(recordKey(col, id));
    return r && !r.deleted ? (r.data as Doc<C>) : undefined;
  }

  /** All live documents in a collection. The array identity is stable until the collection changes. */
  list<C extends CollectionName>(col: C): Doc<C>[] {
    let cached = this.listCache.get(col);
    if (!cached) {
      cached = [];
      for (const r of this.records.values()) if (r.col === col && !r.deleted && r.data) cached.push(r.data);
      this.listCache.set(col, cached);
    }
    return cached as Doc<C>[];
  }

  put<C extends CollectionName>(col: C, doc: Doc<C>): Doc<C> {
    this.write([{ col, id: (doc as { id: string }).id, data: doc, deleted: false }]);
    return doc;
  }

  /**
   * `weak` writes use the oldest possible timestamp, so any version that
   * already exists on the server (e.g. generated defaults another device has
   * since edited) wins when the two meet.
   */
  putMany<C extends CollectionName>(col: C, docs: Doc<C>[], opts: { weak?: boolean } = {}): void {
    this.write(
      docs.map((doc) => ({ col, id: (doc as { id: string }).id, data: doc, deleted: false })),
      opts.weak,
    );
  }

  patch<C extends CollectionName>(col: C, id: string, partial: Partial<Doc<C>>): Doc<C> | undefined {
    const existing = this.get(col, id);
    if (!existing) return undefined;
    const next = { ...existing, ...partial } as Doc<C>;
    this.put(col, next);
    return next;
  }

  remove(col: CollectionName, id: string): void {
    this.write([{ col, id, data: null, deleted: true }]);
  }

  private write(changes: { col: CollectionName; id: string; data: unknown; deleted: boolean }[], weak = false): void {
    const now = Date.now();
    const out: StoredRecord[] = [];
    for (const c of changes) {
      const key = recordKey(c.col, c.id);
      const prev = this.records.get(key);
      // Guarantee monotonic timestamps per record even if the clock jumps back.
      const updatedAt = weak && !prev ? 1 : Math.max(now, (prev?.updatedAt ?? 0) + 1);
      const rec: StoredRecord = {
        key,
        col: c.col,
        id: c.id,
        data: c.deleted ? null : (c.data as SyncRecord['data']),
        deleted: c.deleted || undefined,
        updatedAt,
        device: this.deviceId,
        dirty: true,
      };
      this.records.set(key, rec);
      this.listCache.delete(c.col);
      out.push(rec);
    }
    this.db.putMany(out).catch((err) => console.error('Local save failed', err));
    this.changed();
    for (const l of this.dirtyListeners) l();
  }

  // --- Sync support -------------------------------------------------------

  /** Re-send everything (used when the server's database was replaced). */
  async markAllDirty(): Promise<void> {
    const all = [...this.records.values()].map((r) => ({ ...r, dirty: true }));
    for (const r of all) this.records.set(r.key, r);
    await this.db.putMany(all);
  }

  dirtyRecords(): StoredRecord[] {
    return [...this.records.values()].filter((r) => r.dirty);
  }

  /** Clear dirty flags for records the server accepted, unless they changed again since. */
  async markClean(sent: StoredRecord[]): Promise<void> {
    const cleaned: StoredRecord[] = [];
    for (const s of sent) {
      const cur = this.records.get(s.key);
      if (cur && cur.dirty && cur.updatedAt === s.updatedAt) {
        const rec = { ...cur, dirty: false };
        this.records.set(s.key, rec);
        cleaned.push(rec);
      }
    }
    await this.db.putMany(cleaned);
  }

  /** Merge records from the server. Returns how many changed local state. */
  async applyRemote(records: SyncRecord[]): Promise<number> {
    const changed: StoredRecord[] = [];
    for (const r of records) {
      const key = recordKey(r.col, r.id);
      const cur = this.records.get(key);
      if (cur && !wins(r, cur)) continue;
      const rec: StoredRecord = { ...r, key, dirty: false };
      this.records.set(key, rec);
      this.listCache.delete(r.col);
      changed.push(rec);
    }
    if (changed.length) {
      await this.db.putMany(changed);
      this.changed();
    }
    return changed.length;
  }

  /**
   * The server refused some changes (a sitter link can only add): put back
   * the server's versions, and hide records the server never had.
   */
  async rollBack(restore: SyncRecord[], drop: { col: CollectionName; id: string }[]): Promise<void> {
    const out: StoredRecord[] = [];
    for (const r of restore) out.push({ ...r, key: recordKey(r.col, r.id), dirty: false });
    // A clean tombstone at time 0: gone here, and any real server copy still wins later.
    for (const d of drop) out.push({ col: d.col, id: d.id, data: null, deleted: true, updatedAt: 0, key: recordKey(d.col, d.id), dirty: false });
    if (!out.length) return;
    for (const r of out) {
      this.records.set(r.key, r);
      this.listCache.delete(r.col);
    }
    await this.db.putMany(out);
    this.changed();
  }

  /** Forget everything local (used when switching households). */
  async reset(): Promise<void> {
    this.records.clear();
    this.listCache.clear();
    await this.db.clear();
    this.changed();
  }

  exportAll(): SyncRecord[] {
    return [...this.records.values()].map(({ key: _key, dirty: _dirty, ...r }) => r);
  }

  /** Import a backup: records are merged LWW and pushed on next sync. */
  async importAll(records: SyncRecord[]): Promise<number> {
    const toWrite: StoredRecord[] = [];
    for (const r of records) {
      const key = recordKey(r.col, r.id);
      const cur = this.records.get(key);
      if (cur && !wins(r, cur)) continue;
      const rec: StoredRecord = { ...r, key, dirty: true };
      this.records.set(key, rec);
      this.listCache.delete(r.col);
      toWrite.push(rec);
    }
    await this.db.putMany(toWrite);
    this.changed();
    for (const l of this.dirtyListeners) l();
    return toWrite.length;
  }

  private changed(): void {
    this._version++;
    for (const l of this.listeners) l();
  }
}
